// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import relatedServiceStyles from "@mcmc/App/Widgets/RelatedServices/RelatedServices.style";
import { styledBox } from "@mcmc/XDL/Components/Common/Box";
import { mergeWithCss, Roboto } from "@mcmc/XDL/Utilities";
import { css } from "styled-components";

const wrapper: LinkPresentationProps = {
    color: "text.main",
    typographyProps: {
        ...Roboto,
        css: [
            css`
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                height: 100%;
                width: 100%;
            `,
        ],
    },
    css: [
        css`
            ${styledBox}
            padding: 0;
            margin-bottom: 0;
            height: 100%;
            width: 100%;
            transition: box-shadow 0.2s ease-in-out;

            .read-more-link {
                color: ${getColor("primary")};
            }

            &.linkable-wrapper:hover {
                box-shadow: ${getProp("theme.shadows.2")};

                .read-more-link {
                    color: ${getColor("primary")};
                    text-decoration: underline;
                }
            }

            .bg-image {
                position: relative;
                background-size: cover;
                background-position: center;
            }
        `,
    ],
};

const verticalCardWrapper: LinkPresentationProps = mergeWithCss(wrapper, {
    css: [
        css`
            .bg-image {
                height: 190px;
                flex: 0 0 190px;
            }
            ${typeof document !== "undefined" && navigator?.userAgent.search("Firefox") > -1 && css`
                @media print {
                    break-inside: avoid;
                }
            `}
        `,
    ],
});

const horizontalCardWrapper: LinkPresentationProps = mergeWithCss(wrapper, {
    typographyProps: {
        css: [
            css`
                flex-direction: row;

                .bg-image {
                    flex: 0 0 220px;
                    min-height: 220px;
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                flex-direction: column;

                                .bg-image {
                                    min-height: none;
                                    height: 190px;
                                    flex: 0 0 190px;
                                }
                            `,
                        ],
                        "max",
                    )}
            `,
        ],
    },
});

const textContentWrapper: InjectableCss = {
    css: css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 25px 30px;
        flex: 1 1 auto;
    `,
};

const heading: TypographyPresentationProps = {
    variant: "h5",
    forwardAs: "h2",
    lineHeight: "21px",
    css: [
        css`
            text-transform: none;
            margin-bottom: 12px;
        `,
    ],
};

const verticalCardHeading: TypographyPresentationProps = mergeWithCss(heading, {
    css: css`
        height: 42px;
        overflow: hidden;
    `,
});

const copyText: TypographyPresentationProps = {
    variant: "p",
    size: "14px",
    lineHeight: "20px",
    css: css`
        margin-bottom: 0;
        flex: 1 1 auto;
    `,
};

const linkWrapperStyle: InjectableCss = {
    css: css`
        margin-top: 30px;
        text-transform: uppercase;
    `,
};

const linkStyles: any = {
    alignments: {
        left: {
            css: [
                linkWrapperStyle.css,
                css`
                    text-align: left;
                `,
            ],
        },
        right: {
            css: [
                linkWrapperStyle.css,
                css`
                    text-align: right;
                `,
            ],
        },
        center: {
            css: [
                linkWrapperStyle.css,
                css`
                    text-align: center;
                `,
            ],
        },
    },
    anchor: {
        css: css`
            display: inline-block;
        `,
    },
};

const hiddenImage: LazyImageProps = {
    css: css`
        position: absolute;
        top: 0;
        left: 0;
        height: 0;
        width: 0;
        visibility: hidden;
    `,
};

const childrenWrapper: InjectableCss = {
    css: css`
        margin-bottom: 15px;
    `,
};

const noImageWrapper = mergeWithCss(relatedServiceStyles.noImageWrapper, {
    css: css`
        height: 100%;
    `,
});
const noImageIcon = mergeWithCss(relatedServiceStyles.noImageIcon, {
    size: 80,
});

export interface InfoCardStyles {
    wrapper?: InjectableCss;
    textContentWrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    linkStyles?: any;
    copyText?: TypographyPresentationProps;
    hiddenImage?: LazyImageProps;
    childrenWrapper?: InjectableCss;
    noImageWrapper?: InjectableCss;
    noImageIcon?: IconPresentationProps & { size: number };
}

const styles = {
    vertical: {
        wrapper: verticalCardWrapper,
        textContentWrapper,
        heading: verticalCardHeading,
        linkStyles,
        copyText,
        hiddenImage,
        childrenWrapper,
        noImageWrapper,
        noImageIcon,
    },
    horizontal: {
        wrapper: horizontalCardWrapper,
        textContentWrapper,
        heading,
        linkStyles,
        copyText,
        hiddenImage,
        childrenWrapper,
        noImageWrapper,
        noImageIcon,
    },
};

export function getInfoCardStyles(direction: "vertical" | "horizontal"): InfoCardStyles {
    return styles[direction];
}
